import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { CloseCircleOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Form,
  Button,
  DatePicker,
  Input,
  Typography,
  Avatar,
  Space,
  Table,
  Select,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { journalsQueries, JournalRecordPageShell } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { ParticipantsInfiniteScroll } from "@/features/participants-infinite-scroll";
import { RepresentationModal } from "@/features/representation-modal";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { useUnifiedAutoResize } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import {
  formatLosSignerLabel,
  getLosSignerOptionKey,
} from "../../model/utils/format-los-signer-label";
import { mapSectionItemToFormPayload } from "../../model/utils/map-section-ittem-to-form-payload";
import {
  JournalSectionFormSchema,
  JournalSectionFormInput,
} from "../journals-section-first/journal-section-form/model/types/schema";
import {
  useCreateSectionOneEntry,
  useUpdateSectionOneEntry,
  useUpdateSectionOneWithSign,
} from "../journals-section-first/journal-section-form/model/use-section-one-entry";
import "./journal-section-form-page.scss";

const { Text, Title } = Typography;
const { Option } = Select;

export const JournalSectionFormPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId: itemNoVersionIdParam } = useParams<{ itemNoVersionId?: string }>();
  const { projectId, titleId, mainJournalId, objectId } = useCoordinator();
  const appRoutes = useAppRoutes();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const queryClient = useQueryClient();

  const isEditMode = itemNoVersionIdParam && itemNoVersionIdParam !== "new";
  const itemNoVersionId = isEditMode ? itemNoVersionIdParam : null;

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );
  const sectionOneId = mainJournalData?.mainJournal?.section1?.sectionId || "";

  const { data: currentItemToEdit, isFetching: isItemFetching } = useQuery({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/items/{itemNoVersionId}",
      {
        params: {
          path: { itemNoVersionId: itemNoVersionId || "" },
        },
      },
    ),
    enabled: !!itemNoVersionId,
  });

  const initialValues = useMemo(
    () =>
      currentItemToEdit
        ? mapSectionItemToFormPayload(currentItemToEdit, mainJournalData)
        : null,
    [currentItemToEdit],
  );

  const initUserInfo = useMemo(
    () =>
      currentItemToEdit
        ? {
            name: `${currentItemToEdit?.userCompany?.user?.firstName ?? ""} ${currentItemToEdit?.userCompany?.user?.middleName ?? ""} ${currentItemToEdit?.userCompany?.user?.surname ?? ""}`.trim(),
            organization: currentItemToEdit?.company?.fullName || "",
            position: currentItemToEdit?.userCompany?.user?.position || "",
          }
        : null,
    [currentItemToEdit],
  );

  const { mutate: createEntry, isPending: isCreateLoading } =
    useCreateSectionOneEntry(sectionOneId);
  const { mutate: updateEntry, isPending: isUpdateLoading } =
    useUpdateSectionOneEntry(sectionOneId);
  const { mutate: updateEntryWithSign, isPending: isUpdateWithSignLoading } =
    useUpdateSectionOneWithSign(sectionOneId);

  const isSaving =
    isCreateLoading || isUpdateLoading || isUpdateWithSignLoading;
  const isFormLoading = Boolean(
    isEditMode && isItemFetching && !currentItemToEdit,
  );
  const showPageLoader = isFormLoading || isSaving;
  const loaderTip = isSaving ? "Сохранение..." : "Загрузка...";

  const [key, setKey] = useState(new Date().getTime());
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<JournalSectionFormInput>({
    resolver: zodResolver(JournalSectionFormSchema),
    defaultValues: {
      organization: {
        company: { companyId: "" },
        projectParticipantId: "",
      },
      userCompany: null,
      workType: "",
      startDate: null,
      endDate: null,
      signer: "",
      actionDetails: "",
    },
  });
  const organization = watch("organization");

  const userCompany = useWatch({
    control,
    name: "userCompany",
  });

  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );


  useEffect(() => {
    if (initialValues) {
      setValue("startDate", dayjs(initialValues.dateStart));
      setValue(
        "endDate",
        initialValues.dateEnd ? dayjs(initialValues.dateEnd) : null,
      );
      setValue("workType", initialValues?.workType ?? "");
      setValue("signer", initialValues?.signer ?? "");

      setValue("organization", {
        company: {
          companyId: initialValues?.company?.companyId,
        },
        projectParticipantId: initialValues?.company?.projectParticipantId,
      });

      setValue("userCompany", {
        userId: initialValues?.userCompany?.userId || "",
        userCompanyHistoryId:
          initialValues?.userCompany?.userCompanyHistoryId || "",
      });
    }
  }, [initialValues, setValue]);

  const signersList = useMemo(
    () => [
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
        ?.representatives ?? []),
      ...(titleJournal?.mainJournal?.titleJournal?.buildingContractor
        ?.constructionControls ?? []),
    ],
    [titleJournal],
  );

  const handleClose = () => {
    if (showPageLoader) return;
    navigate(
      appRoutes.journals.section1(projectId, objectId, mainJournalId, titleId),
    );
  };

  const onSubmit = async (formData: any) => {
    if (!formData.userCompany) return;
    if (initialValues?.status === "SIGNED") {
      const versionId = currentItemToEdit?.currentVersionId || "";
      updateEntryWithSign(
        {
          body: {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section1Id: mainJournalData?.mainJournal?.section1?.sectionId || "",
            section1ListId:
              mainJournalData?.mainJournal?.section1?.sectionListId || "",
            company: {
              companyId: formData.organization.company.companyId,
              projectParticipantId: formData.organization.projectParticipantId,
            },
            userCompany: {
              userCompanyHistoryId:
                formData.userCompany.userCompanyHistoryId ||
                initialValues?.userCompany?.userCompanyHistoryId ||
                "",
              userId: formData.userCompany.userId,
            },
            workType: formData.workType,
            dateStart: formData.startDate.toISOString(),
            dateEnd: formData.endDate?.toISOString() || null,
            signer: formData.signer,
            actionDetails: formData.actionDetails || "",
          },
          params: {
            path: {
              sectionOneListId:
                mainJournalData?.mainJournal?.section1?.sectionListId || "",
              versionId,
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: journalsQueries.getSection1({
                sectionOneId,
              }).queryKey,
            });
            reset();
            navigate(-1);
          },
        },
      );
    } else if (initialValues?.userCompany?.userId) {
      updateEntry(
        {
          body: {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section1Id: mainJournalData?.mainJournal?.section1?.sectionId || "",
            section1ListId:
              mainJournalData?.mainJournal?.section1?.sectionListId || "",
            company: {
              companyId: formData.organization.company.companyId,
              projectParticipantId: formData.organization.projectParticipantId,
            },
            userCompany: {
              userCompanyHistoryId:
                formData.userCompany.userCompanyHistoryId ||
                initialValues?.userCompany?.userCompanyHistoryId ||
                "",
              userId: formData.userCompany.userId,
            },
            workType: formData.workType,
            dateStart: formData.startDate.toISOString(),
            dateEnd: formData.endDate?.toISOString() || null,
            signer: formData.signer,
          },
          params: {
            path: {
              itemNoVersionId:
                initialValues?.itemNoVersionId || itemNoVersionId || "",
            },
          },
        },
        {
          onSuccess: () => {
            reset();
            navigate(-1);
          },
        },
      );
    } else {
      createEntry(
        {
          body: {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section1Id: mainJournalData?.mainJournal?.section1?.sectionId || "",
            section1ListId:
              mainJournalData?.mainJournal?.section1?.sectionListId || "",
            company: {
              companyId: formData.organization.company.companyId,
              projectParticipantId: formData.organization.projectParticipantId,
            },
            userCompany: {
              userCompanyHistoryId:
                formData.userCompany.userCompanies?.[0]?.userCompaniesHistoryId,
              userId: formData.userCompany.userId,
            },
            workType: formData.workType,
            dateStart: formData.startDate.toISOString(),
            dateEnd: formData.endDate?.toISOString() || null,
            signer: formData.signer,
          },
          params: {
            path: {
              sectionOneId:
                mainJournalData?.mainJournal?.section1?.sectionId || "",
            },
          },
        },
        {
          onSuccess: () => {
            reset();
            navigate(-1);
          },
        },
      );
    }
  };

  const columns = [
    {
      title: "ФИО",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: any) => (
        <Space size="middle">
          <Avatar>{record.initials}</Avatar>
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Организация",
      dataIndex: "organization",
      key: "organization",
      render: (organization: string) => <Text>{organization}</Text>,
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
      render: (position: string) => <Text>{position}</Text>,
    },
    {
      key: "action",
      render: () => (
        <Button
          type="text"
          icon={<CloseCircleOutlined style={{ color: "red" }} />}
          danger
          onClick={() => setValue("userCompany", null)}
        />
      ),
    },
  ];

  const tableData = useMemo(() => {
    if (!userCompany) return [];

    return [
      {
        key: userCompany?.userCompanies?.[0]?.userCompaniesHistoryId ?? "-",
        name:
          `${userCompany?.userSurname ?? ""} ${userCompany?.userFirstName ?? ""} ${userCompany?.userMiddleName ?? ""}`.trim() ||
          initUserInfo?.name ||
          "-",
        organization:
          userCompany?.userCompanies?.[0]?.shortName ||
          initUserInfo?.organization ||
          "-",
        position:
          userCompany?.userCompanies?.[0]?.position ||
          initUserInfo?.position ||
          "-",
        initials:
          (userCompany?.userSurname?.[0] ?? "") +
            (userCompany?.userFirstName?.[0] ?? "") +
            (userCompany?.userMiddleName?.[0] ?? "") || "-",
        email: userCompany?.userEmail ?? "-",
        phone: userCompany?.userMobilePhone ?? "-",
        role: "–",
        nostroyId: "–",
      },
    ];
  }, [
    // Use primitive fields only to avoid re-computing on every render
    // due to unstable object references from useWatch
    userCompany?.userId,
    userCompany?.userSurname,
    userCompany?.userFirstName,
    userCompany?.userMiddleName,
    userCompany?.userEmail,
    userCompany?.userMobilePhone,
    userCompany?.userCompanies?.[0]?.userCompaniesHistoryId,
    userCompany?.userCompanies?.[0]?.shortName,
    userCompany?.userCompanies?.[0]?.position,
    initUserInfo?.name,
    initUserInfo?.organization,
    initUserInfo?.position,
  ]);

  const title = isEditMode
    ? "Редактирование записи"
    : "Добавление записи в раздел 1 ОЖР";

  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
      <Spin
        spinning={showPageLoader}
        size="large"
        tip={loaderTip}
        className="journal-section-form-page-spin"
      >
        <div className="journal-section-form-page-title-block">
          <Title level={3}>{title}</Title>
        </div>
        {initialValues?.status === "SIGNED" && (
          <div className="edit-notice">
            В случае внесения изменений в записи общего журнала в
            электронной форме создается новая версия записи. Запись нужно
            будет подписать заново. Понадобится заполнить поле о причинах
            изменений.
          </div>
        )}
        <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
        <Form.Item
          label="Организация, осуществляющая строительство, реконструкцию, капитальный ремонт"
          validateStatus={
            errors.organization?.projectParticipantId ||
            errors.organization?.company?.companyId
              ? "error"
              : ""
          }
          help={
            errors.organization?.projectParticipantId?.message ??
            errors.organization?.company?.companyId?.message
          }
        >
          <Controller
            name="organization"
            control={control}
            render={({ field }) => (
              <ParticipantsInfiniteScroll
                projectId={projectId}
                onPartySelect={(value) => {
                  field.onChange(value);
                  setValue("userCompany", null);
                  setKey(Date.now());
                }}
                value={field?.value?.projectParticipantId || ""}
              />
            )}
          />
        </Form.Item>

        {userCompany && (
          <Form.Item label="Лицо, входящее в список инженерно-технического персонала">
            <Table
              dataSource={tableData}
              columns={columns}
              pagination={false}
              showHeader
              bordered={false}
              style={{ width: "100%" }}
              key={key}
            />
          </Form.Item>
        )}

        {tableData.length === 0 && (
          <Form.Item
            style={{
              marginBottom: 24,
              flexDirection: "column",
              display: "flex",
            }}
          >
            <Button
              type="primary"
              onClick={onOpen}
              disabled={!organization?.projectParticipantId}
            >
              Добавить сотрудника
            </Button>
            <div className="journal-form-error">
              {errors.userCompany?.message?.toString()}
            </div>
          </Form.Item>
        )}

        <div className="form-dates">
          <Form.Item
            label="Дата начала работ"
            validateStatus={errors.startDate ? "error" : ""}
            help={errors.startDate?.message?.toString()}
          >
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date)}
                  style={{ width: "100%" }}
                  format="DD.MM.YYYY"
                  placeholder="дд.мм.гггг"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label="Дата окончания работ (необязательно)"
            validateStatus={errors.endDate ? "error" : ""}
            help={errors.endDate?.message?.toString()}
          >
            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) => field.onChange(date)}
                  disabledDate={(current) =>
                    watch("startDate") &&
                    current &&
                    current.isBefore(dayjs(watch("startDate")), "day")
                  }
                  style={{ width: "100%" }}
                  format="DD.MM.YYYY"
                  placeholder="дд.мм.гггг"
                />
              )}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Вид работ"
          validateStatus={errors.workType ? "error" : ""}
          help={errors.workType?.message?.toString()}
        >
          <Controller
            name="workType"
            control={control}
            rules={{ required: "Укажите вид работ" }}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Укажите вид работ"
                autoComplete="off"
                maxLength={255}
                showCount
              />
            )}
          />
        </Form.Item>

        <Form.Item
          label="Уполномоченный представитель лица, осуществляющего строительство, реконструкцию, кап. ремонт"
          validateStatus={errors.signer ? "error" : ""}
          help={errors.signer?.message?.toString()}
        >
          <Controller
            name="signer"
            control={control}
            rules={{ required: "Выберите представителя" }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Выберите представителя"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {signersList.map((signer) => (
                  <Option
                    key={getLosSignerOptionKey(signer)}
                    value={signer.user?.userId}
                  >
                    {formatLosSignerLabel(signer)}
                  </Option>
                ))}
              </Select>
            )}
          />
          <div className="journal-form-hint">
            Этому человеку будет отправлена данная запись на подписание
          </div>
        </Form.Item>

        {initialValues?.status === "SIGNED" && (
          <Form.Item
            label="Причина изменения записи"
            validateStatus={errors.actionDetails ? "error" : ""}
            help={errors.actionDetails?.message?.toString()}
          >
            <Controller
              name="actionDetails"
              control={control}
              rules={{ required: "Укажите причина изменения записи" }}
              render={({ field }) => (
                <Input.TextArea
                  {...field}
                  placeholder="Опишите причина изменения записи"
                  rows={4}
                  maxLength={255}
                  showCount
                />
              )}
            />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ height: 44 }}
            disabled={showPageLoader}
          >
            {initialValues?.status === "SIGNED" && "Сохранить"}
            {initialValues?.status !== "SIGNED" &&
              initialValues &&
              "Обновить запись"}
            {initialValues?.status !== "SIGNED" &&
              !initialValues &&
              "Добавить запись"}
          </Button>
          <Button
            onClick={handleClose}
            disabled={showPageLoader}
            style={{ marginLeft: 8, height: 44 }}
          >
            Отмена
          </Button>
        </Form.Item>
        </Form>
      </Spin>

      <RepresentationModal
        isOpen={isOpen}
        handleClose={onClose}
        projectParticipantId={organization?.projectParticipantId}
        handleAdd={(_, user) => {
          // @ts-expect-error UserInfo из RepresentationModal не совпадает с типом userCompany формы
          setValue("userCompany", user);
          onClose();
        }}
        key={key}
      />
    </JournalRecordPageShell>
  );
};
